import { call, put } from 'redux-saga/effects';
import {
  SET_BOOKING_REFERENCE_LIST,
  SET_BOOKING_REFERENCE_FETCHING,
  SET_BOOKING_REFERENCE_UPLOAD_SUBMITTING,
  SET_BOOKING_COPY_UPDATE_SUBMITTING,
  SET_BOOKING_OCR_DELETE_SUBMITTING,
  SET_BOOKING_BL_MANAGE_SUBMITTING,
  SET_BOOKING_OCR_REPORT_DOWNLOADING,
  START_LOADER,
  END_LOADER,
  TOAST_SUCCESS,
  TOAST_ERROR,
} from 'Constants/actionConstants';
import {
  getDataWithToken,
  postDataWithToken,
  putDataWithToken,
  putFileWithToken,
  deleteDataWithToken,
  downloadFileWithToken,
  ocrBookingDocument,
  BOOKING_REFERENCE_LIST_URL,
  BOOKING_REFERENCE_UPLOAD_URL,
  UPDATE_BOOKING_COPY_URL,
  DELETE_BOOKING_OCR_DATA_URL,
  MANAGE_BOOKING_BL_URL,
  DOWNLOAD_BOOKING_OCR_REPORT_URL,
  DOWNLOAD_FILE_URL,
} from 'Utils/api';

function mapDocToUi(detail) {
  return {
    id: detail.file_id,
    name: detail.file_name || '',
    bookingDate: detail.booking_date || '',
    bookingNo: detail.booking_no || '',
    product: detail.product_commodity || '',
    receiptPlace: detail.place_of_receipt || '',
    loadingPort: detail.port_of_loading || '',
    dischargePort: detail.port_of_discharge || '',
    srcCountry: detail.source_country || '',
    destCountry: detail.destination_country || '',
    vessel: detail.vessel_voyage || '',
    etdAtPol: detail.vessel_etd_at_pol || '',
    expectedDelivery: detail.expected_date_of_delivery || '',
    noOfContainers: detail.no_of_containers || '',
    containerSize: detail.container_size || '',
    containerType: detail.container_type || '',
    vendorName: detail.vendor_name || '',
    shippingLine: detail.shipping_line || '',
    transshipmentPort: detail.transshipment_port || '',
    transshipmentEta: detail.transshipment_eta || '',
    transshipmentVesselVoyage: detail.transshipment_vessel_voyage || '',
    ocr_status: detail.ocr_status || '',
    blDocs: (detail.bl_data || []).map(bl => ({
      id: bl.bl_id,
      blNumber: bl.bl_number || '',
      attachment: bl.bl_filename || '',
      size: '',
      uploadedAt: bl.bl_uploaded ? new Date(bl.bl_uploaded).toLocaleString() : '',
      presigned_url: bl.presigned_url || '',
    })),
  };
}

function mapHeadingChargeToUi(charge) {
  return {
    id: charge.cost_charge_id,
    headingId: charge.headingChargesID,
    name: charge.headingChargesValue || '',
    description: charge.headingChargesDescription || '',
    isBaseFreight: charge.isBaseFreight || false,
    unitName: charge.headingChargesUnitName || '',
    unitSymbol: charge.headingChargesUnitSymbol || '',
    volumeValue: charge.headingChargesBusinessVolumeValue || 0,
    volumeUnitName: charge.headingChargesBusinessVolumeUnitName || '',
    ceilingPrice: charge.ceiling_price || 0,
    budgetInr: charge.headingChargesBudget_inr || 0,
    budgetUsd: charge.headingChargesBudget_usd || 0,
    cpInr: charge.headingChargesCp_inr || 0,
    cpUsd: charge.headingChargesCp_usd || 0,
  };
}

function mapRowToUi(row) {
  return {
    id: `${row.rfq_id}_${row.transporter_id}_${row.liner_id}_${(row.to || '').replace(/\s+/g, '_')}`,
    rfq_id: row.rfq_id,
    transporter_id: row.transporter_id,
    liner_id: row.liner_id,
    trip_id: row.trip_id,
    rfqNumber: row.rfq_number || '',
    shippingLiner: row.liner_name || '',
    serviceProvider: row.transporter_name || '',
    from: row.from || '',
    to: row.to || '',
    containerType: row.container_type || '',
    containers: row.no_of_containers || 0,
    deskType: row.desk_name || '',
    incoTerm: row.inco_term || '',
    biddingEvent: row.bidding_event || '',
    loiAcceptedDate: row.loi_accepted_date || null,
    bookingUpdatedAt: row.booking_updated_at || null,
    has_booking_copy: row.has_booking_copy || false,
    docs: row.has_booking_copy && row.booking_copy_details
      ? row.booking_copy_details.map(mapDocToUi)
      : [],
    headingCharges: (row.multi_mode_heading_charges || []).map(mapHeadingChargeToUi),
  };
}

export function* getBookingReferenceList(action) {
  try {
    yield put({ type: START_LOADER });
    yield put({ type: SET_BOOKING_REFERENCE_FETCHING, payload: true });
    const data = yield call(getDataWithToken, BOOKING_REFERENCE_LIST_URL, action.params || {});
    yield put({ type: END_LOADER });
    if (data && data.status) {
      const { list = [], total = 0, page = 0, size = 20 } = data.data || {};
      yield put({
        type: SET_BOOKING_REFERENCE_LIST,
        payload: {
          list: list.map(mapRowToUi),
          total,
          page,
          size,
        },
      });
    } else {
      yield put({ type: SET_BOOKING_REFERENCE_FETCHING, payload: false });
      yield put({ type: TOAST_ERROR, message: (data && data.message) || 'Failed to fetch booking list.' });
    }
    if (action.onSuccess) action.onSuccess(data);
  } catch (e) {
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_REFERENCE_FETCHING, payload: false });
    yield put({ type: TOAST_ERROR, message: e && e.message ? e.message : 'Oops! Something Went Wrong' });
    if (action.onError) action.onError(e);
  }
}

export function* uploadBookingReference(action) {
  try {
    yield put({ type: START_LOADER });
    yield put({ type: SET_BOOKING_REFERENCE_UPLOAD_SUBMITTING, payload: true });
    const { file } = action.data;

    const ocrResult = yield call(ocrBookingDocument, file);

    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_REFERENCE_UPLOAD_SUBMITTING, payload: false });
    yield put({ type: TOAST_SUCCESS, message: 'Booking copy processed successfully.' });
    if (action.onSuccess) action.onSuccess(ocrResult);
  } catch (e) {
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_REFERENCE_UPLOAD_SUBMITTING, payload: false });
    yield put({ type: TOAST_ERROR, message: e && e.message ? e.message : 'Oops! Something Went Wrong' });
    if (action.onError) action.onError(e);
  }
}

export function* saveBookingReference(action) {
  try {
    yield put({ type: START_LOADER });
    yield put({ type: SET_BOOKING_REFERENCE_UPLOAD_SUBMITTING, payload: true });
    const { params } = action.data;
    const data = yield call(postDataWithToken, BOOKING_REFERENCE_UPLOAD_URL, params);
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_REFERENCE_UPLOAD_SUBMITTING, payload: false });
    if (data && data.status) {
      yield put({ type: TOAST_SUCCESS, message: data.message || 'Booking copy saved successfully.' });
      if (action.onSuccess) action.onSuccess(data);
    } else {
      yield put({ type: TOAST_ERROR, message: (data && data.message) || 'Save failed.' });
      if (action.onError) action.onError(data);
    }
  } catch (e) {
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_REFERENCE_UPLOAD_SUBMITTING, payload: false });
    yield put({ type: TOAST_ERROR, message: e && e.message ? e.message : 'Oops! Something Went Wrong' });
    if (action.onError) action.onError(e);
  }
}

export function* updateBookingCopy(action) {
  try {
    yield put({ type: START_LOADER });
    yield put({ type: SET_BOOKING_COPY_UPDATE_SUBMITTING, payload: true });
    const data = yield call(putDataWithToken, UPDATE_BOOKING_COPY_URL, action.data);
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_COPY_UPDATE_SUBMITTING, payload: false });
    if (data && data.status) {
      yield put({ type: TOAST_SUCCESS, message: data.message || 'Booking copy updated successfully.' });
      if (action.onSuccess) action.onSuccess(data);
    } else {
      yield put({ type: TOAST_ERROR, message: (data && data.message) || 'Update failed.' });
      if (action.onError) action.onError(data);
    }
  } catch (e) {
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_COPY_UPDATE_SUBMITTING, payload: false });
    yield put({ type: TOAST_ERROR, message: e && e.message ? e.message : 'Oops! Something Went Wrong' });
    if (action.onError) action.onError(e);
  }
}

export function* deleteBookingOcrData(action) {
  try {
    yield put({ type: START_LOADER });
    yield put({ type: SET_BOOKING_OCR_DELETE_SUBMITTING, payload: true });
    const data = yield call(deleteDataWithToken, DELETE_BOOKING_OCR_DATA_URL, {}, action.data);
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_OCR_DELETE_SUBMITTING, payload: false });
    if (data && data.status) {
      yield put({ type: TOAST_SUCCESS, message: data.message || 'OCR data deleted successfully.' });
      if (action.onSuccess) action.onSuccess(data);
    } else {
      yield put({ type: TOAST_ERROR, message: (data && data.message) || 'Delete failed.' });
      if (action.onError) action.onError(data);
    }
  } catch (e) {
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_OCR_DELETE_SUBMITTING, payload: false });
    yield put({ type: TOAST_ERROR, message: e && e.message ? e.message : 'Oops! Something Went Wrong' });
    if (action.onError) action.onError(e);
  }
}

export function* manageBookingBL(action) {
  try {
    yield put({ type: START_LOADER });
    yield put({ type: SET_BOOKING_BL_MANAGE_SUBMITTING, payload: true });
    const { file, params } = action.data;
    // BRD: PUT multipart/form-data for both add (with/without file) and delete
    const data = yield call(putFileWithToken, MANAGE_BOOKING_BL_URL, file || null, params);
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_BL_MANAGE_SUBMITTING, payload: false });
    if (data && data.status) {
      yield put({ type: TOAST_SUCCESS, message: data.message || 'BL operation completed successfully.' });
      if (action.onSuccess) action.onSuccess(data.data);
    } else {
      yield put({ type: TOAST_ERROR, message: (data && data.message) || 'BL operation failed.' });
      if (action.onError) action.onError(data);
    }
  } catch (e) {
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_BL_MANAGE_SUBMITTING, payload: false });
    yield put({ type: TOAST_ERROR, message: e && e.message ? e.message : 'Oops! Something Went Wrong' });
    if (action.onError) action.onError(e);
  }
}

export function* downloadBookingOcrReport(action) {
  try {
    yield put({ type: START_LOADER });
    yield put({ type: SET_BOOKING_OCR_REPORT_DOWNLOADING, payload: true });
    yield call(downloadFileWithToken, DOWNLOAD_BOOKING_OCR_REPORT_URL, action.params || {}, 'BookingReferenceOCRReport.xlsx');
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_OCR_REPORT_DOWNLOADING, payload: false });
    if (action.onSuccess) action.onSuccess();
  } catch (e) {
    yield put({ type: END_LOADER });
    yield put({ type: SET_BOOKING_OCR_REPORT_DOWNLOADING, payload: false });
    yield put({ type: TOAST_ERROR, message: e && e.message ? e.message : 'Download failed.' });
    if (action.onError) action.onError(e);
  }
}

export function* downloadBlFile(action) {
  try {
    yield put({ type: START_LOADER });
    const data = yield call(getDataWithToken, DOWNLOAD_FILE_URL, { fileName: action.fileName });
    yield put({ type: END_LOADER });
    if (data && data.url) {
      const link = document.createElement('a');
      link.href = data.url;
      link.download = action.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    if (action.onSuccess) action.onSuccess();
  } catch (e) {
    yield put({ type: END_LOADER });
    yield put({ type: TOAST_ERROR, message: e && e.message ? e.message : 'Download failed.' });
    if (action.onError) action.onError(e);
  }
}
